/* Medicus — embroidery preview on the FABRIC (27 Oct 2026)
 * ===========================================================================
 * WHY. Customers keep choosing a small, thin, white font on a busy print, and
 * the old preview could not warn them: it drew the text on a photograph of a
 * PLAIN navy cap, where white always looks fine. This draws the same text, at
 * the same true size, over the product's own flat-lay photograph, and checks
 * how much of the print around the letters is close to the thread colour.
 *
 * HOW IT FITS. It wraps MCEmbroidery.mount() (assets/mc-embroidery.js) and adds
 * a second canvas next to the cap one. The cap view, the width meter and the
 * sentence are untouched and still there behind the "On a cap" tab. Load order
 * matters: this file must load AFTER mc-embroidery.js and BEFORE
 * mc-embroidery-live.js, which is how snippets/mc-embroidery.liquid lists it.
 *
 * WHERE THE FABRIC COMES FROM. snippets/mc-embroidery.liquid picks the image:
 *   1. metafield custom.fabric_flat_lay, if a product ever has one;
 *   2. otherwise the first product image whose alt text says it is a flat lay
 *      ("ruler", "shown flat", "fabric pattern", "fabric texture" ...);
 *   3. otherwise the featured photo, cropped to the cap (scale approximate).
 *
 * SCALE. Every flat lay so far is shot on the same 1 m ruler, with about
 * 33 cm to 64 cm in frame: the image is ~31.8 cm across and the ruler starts
 * ~83.5% of the way down. When the ruler is found the drawing is to scale.
 * When it is not, the same width is assumed and the caption says approximate.
 *
 * READ-ONLY, same rule as the rest of the embroidery preview. It never touches
 * a YMQ field. Delete the one script line in mc-embroidery.liquid to remove it.
 * =========================================================================== */
(function () {
  'use strict';

  var M = window.MCEmbroidery;
  var CFG = window.MCEmbroideryConfig || {};
  var F = CFG.fabric;
  if (!M || !M.mount || !M._internal || !F || !F.src) return;
  var I = M._internal;

  /* ---- the numbers ------------------------------------------------------ */
  var RULER_CM = 31.8;     /* cm across a ruler-template flat lay             */
  var RULER_TOP = 0.835;   /* the fabric stops here; the ruler is below it     */
  var PITCH = 1.5;         /* baseline to baseline, in cap heights (machine)   */
  /* The readability check, all tunable from the snippet config without
     touching this file (MCEmbroideryConfig.fabricCheck).
       blend   contrast ratio under which the thread meets the print. 3 is the
               level WCAG asks of large text; under it, letters fade into it.
       limits  share of the area round the letters that is under `blend`,
               before the pill says "Busy" and then "Hard to read".
       thin    stricter limits for a thin font or a small size, because a
               thin stroke disappears into a busy print sooner. */
  var CHK = CFG.fabricCheck || {};
  var BLEND = CHK.blend || 3.0;
  var LIMITS = {
    normal: CHK.normal || { busy: 0.10, poor: 0.30 },
    thin:   CHK.thin   || { busy: 0.04, poor: 0.20 }
  };
  var THIN_FONTS = CHK.thinFonts || { script: 1, garamond: 1 };
  var THIN_MM = CHK.thinMm || 12;
  /* How much the print shows in the thread (see stitch(), step 5). */
  var MULTIPLY = q(CFG.fabricMultiply, CHK.multiply, 0.3);
  var SHOW = q(CFG.fabricShow, CHK.show, 0.8);
  var OPACITY = q(CFG.fabricOpacity, CHK.opacity, 0.96);
  function q(a, b, d) { return typeof a === 'number' ? a : typeof b === 'number' ? b : d; }          /* Size 2 and under counts as small */
  function isThin(st) {
    if (THIN_FONTS[st.font]) return true;
    var ls = norm(st), mm = 99;
    for (var i = 0; i < ls.length; i++) mm = Math.min(mm, ls[i].sizeMm);
    return mm <= THIN_MM;
  }

  var COPY = {
    tabFabric: 'On the fabric',
    tabCap: 'On a cap',
    clear: 'Easy to read',
    busy: 'Busy behind the text',
    poor: 'Hard to read',
    sayClear: 'Your thread colour stands out well from this print.',
    sayBusy: 'Some of this print is close to your thread colour, so letters will fade where they cross it.',
    sayPoor: 'A lot of this print is close to your thread colour, so the text will be hard to read once it is stitched.',
    thinTip: ' Thin or small lettering loses the most on a busy print: a bolder font or a bigger size holds up better.',
    sizeTip: ' A bigger size or a bolder font holds up better.',
    altTip: ' {colour} thread would stand out far better on this one.',
    outlineTip: ' Outlined text also helps on a busy print.',
    exact: 'Drawn to scale on a photo of this fabric, {cm} cm across.',
    approx: 'Drawn on a product photo, so the size of the print is approximate.'
  };
  for (var ck in (CFG.fabricCopy || {})) COPY[ck] = CFG.fabricCopy[ck];

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  /* ---- colour ----------------------------------------------------------- */
  var _rgb = {};
  function rgbOf(css) {
    if (_rgb[css]) return _rgb[css];
    var c = document.createElement('canvas').getContext('2d');
    c.fillStyle = '#000'; c.fillStyle = css || '#f2f3f5';
    var v = c.fillStyle, m;
    if ((m = /^#([0-9a-f]{6})$/i.exec(v))) {
      var n = parseInt(m[1], 16);
      return (_rgb[css] = [n >> 16 & 255, n >> 8 & 255, n & 255]);
    }
    m = v.match(/[\d.]+/g) || [242, 243, 245];
    return (_rgb[css] = [+m[0], +m[1], +m[2]]);
  }
  var LIN = [];
  for (var li = 0; li < 256; li++) {
    var s = li / 255;
    LIN[li] = s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  }
  function lum(r, g, b) { return 0.2126 * LIN[r] + 0.7152 * LIN[g] + 0.0722 * LIN[b]; }
  function ratio(a, b) { return a > b ? (a + 0.05) / (b + 0.05) : (b + 0.05) / (a + 0.05); }

  /* ---- the fabric image ------------------------------------------------- */
  /* Is there a ruler along the bottom? It is a pale, flat grey band. Sampled
     on the left, where there is only a scale and no logo. */
  function hasRuler(im) {
    try {
      var cv = document.createElement('canvas'); cv.width = cv.height = 64;
      var c = cv.getContext('2d');
      c.drawImage(im, 0, 0, 64, 64);
      var d = c.getImageData(2, 57, 20, 5).data, L = 0, S = 0, n = 0;
      for (var i = 0; i < d.length; i += 4) {
        var mx = Math.max(d[i], d[i + 1], d[i + 2]), mn = Math.min(d[i], d[i + 1], d[i + 2]);
        L += (d[i] + d[i + 1] + d[i + 2]) / 3; S += mx ? (mx - mn) / mx : 0; n++;
      }
      /* Measured on four shoots: 137-140 brightness, 0.11-0.12 saturation. */
      return L / n > 110 && S / n < 0.16;
    } catch (e) { return false; }
  }

  var _fab = null;
  function fabric() {
    if (_fab) return _fab;
    _fab = I.image(F.src).then(function (im) {
      var W = im.naturalWidth || im.width, H = im.naturalHeight || im.height;
      var f = { im: im, W: W, H: H };
      if (F.kind === 'flat') {
        f.exact = hasRuler(im);
        f.box = { x: 0, y: 0, w: W, h: f.exact ? Math.round(H * RULER_TOP) : H };
        f.pxPerCm = W / (F.cmAcross || RULER_CM);
      } else {
        var k = F.crop || { x: 0.55, y: 0.27, w: 0.42, h: 0.27 };
        f.exact = false;
        f.box = { x: (k.x - k.w / 2) * W, y: (k.y - k.h / 2) * H, w: k.w * W, h: k.h * H };
        f.pxPerCm = f.box.w / (F.photoCmAcross || 14);
      }
      /* Product photos come out darker than flat lays; this lifts their
         brightest 2% to where a flat lay's sit (used by the thread drawing). */
      f.gain = 1;
      if (!f.exact && F.kind !== 'flat') {
        try {
          var gc = document.createElement('canvas'); gc.width = gc.height = 96;
          var gx = gc.getContext('2d');
          gx.drawImage(im, f.box.x, f.box.y, f.box.w, f.box.h, 0, 0, 96, 96);
          var gd = gx.getImageData(0, 0, 96, 96).data, mx = [];
          for (var gi = 0; gi < gd.length; gi += 4) mx.push(Math.max(gd[gi], gd[gi + 1], gd[gi + 2]));
          mx.sort(function (a, b) { return b - a; });
          var p98 = mx[Math.floor(mx.length * 0.02)] || 240;
          f.gain = Math.max(1, Math.min(1.4, 240 / p98));
        } catch (e) {}
      }
      return f;
    });
    return _fab;
  }

  /* ---- drawing ---------------------------------------------------------- */
  function norm(st) {
    return (st.lines || []).map(function (l) {
      if (typeof l === 'string') return { text: l, size: st.size, sizeMm: st.sizeMm };
      return { text: l.text || '', size: l.size || st.size, sizeMm: l.sizeMm || st.sizeMm };
    }).filter(function (l) { return l.text && l.text.trim(); });
  }

  function textAt(c, font, capNat, d, x, y) {
    c.save();
    c.translate(x, y);
    c.scale(d.stretch, 1);
    c.font = I.fontCss(font, 200 * (d.cap / capNat));
    c.textAlign = 'center';
    c.textBaseline = 'alphabetic';
    c.fillText(d.text, 0, 0);
    c.restore();
  }

  /* The readability check. Works at half the display scale, which is plenty
     for "how much of this print is near the thread colour". */
  function judge(fab, font, capNat, ds, ys, ppc, st, CW) {
    var res = { verdict: 'clear', share: 0, alt: null };
    try {
      var a = 0.5, i, k;
      /* the text block as a mask, cropped to its own box */
      var x0 = CW, x1 = 0, y0 = Infinity, y1 = 0, mc0 = document.createElement('canvas').getContext('2d');
      for (i = 0; i < ds.length; i++) {
        mc0.font = I.fontCss(font, 200 * (ds[i].cap / capNat));
        var w = mc0.measureText(ds[i].text).width * ds[i].stretch;
        x0 = Math.min(x0, CW / 2 - w / 2); x1 = Math.max(x1, CW / 2 + w / 2);
        y0 = Math.min(y0, ys[i] - ds[i].cap * 1.15); y1 = Math.max(y1, ys[i] + ds[i].cap * 0.45);
      }
      var pad = ds[0].cap * 0.2;
      x0 -= pad; x1 += pad; y0 -= pad; y1 += pad;
      var MW = Math.max(1, Math.round((x1 - x0) * a)), MH = Math.max(1, Math.round((y1 - y0) * a));
      var mk = document.createElement('canvas'); mk.width = MW; mk.height = MH;
      var mc = mk.getContext('2d');
      mc.scale(a, a); mc.translate(-x0, -y0);
      mc.fillStyle = '#000'; mc.strokeStyle = '#000'; mc.lineJoin = 'round';
      for (i = 0; i < ds.length; i++) {
        mc.save();
        mc.lineWidth = Math.max(2, ds[i].cap * 0.22);
        mc.translate(CW / 2, ys[i]); mc.scale(ds[i].stretch, 1);
        mc.font = I.fontCss(font, 200 * (ds[i].cap / capNat));
        mc.textAlign = 'center'; mc.textBaseline = 'alphabetic';
        mc.strokeText(ds[i].text, 0, 0); mc.fillText(ds[i].text, 0, 0);
        mc.restore();
      }
      var m = mc.getImageData(0, 0, MW, MH).data;

      /* the whole fabric at the same scale */
      var s = ppc * a / fab.pxPerCm;
      var FW = Math.round(fab.box.w * s), FH = Math.round(fab.box.h * s);
      if (FW < MW || FH < MH) { FW = Math.max(FW, MW); FH = Math.max(FH, MH); }
      var fc = document.createElement('canvas'); fc.width = FW; fc.height = FH;
      var fx = fc.getContext('2d');
      fx.drawImage(fab.im, fab.box.x, fab.box.y, fab.box.w, fab.box.h, 0, 0, FW, FH);

      /* A product photo is shot on a lit mannequin and comes out darker than
         a flat lay, which flatters light thread. Lift it so its brightest
         highlights sit where a flat lay's do. */
      var gain = 1;
      if (!fab.exact && F.kind !== 'flat') {
        var all = fx.getImageData(0, 0, FW, FH).data, hist = new Array(256), tot = 0, j;
        for (j = 0; j < 256; j++) hist[j] = 0;
        for (j = 0; j < all.length; j += 16) { hist[Math.max(all[j], all[j + 1], all[j + 2])]++; tot++; }
        for (j = 255, k = 0; j > 0; j--) { k += hist[j]; if (k >= tot * 0.02) break; }
        if (j > 0) gain = Math.max(1, Math.min(1.4, 240 / j));
      }
      function g8(v) { return Math.min(255, Math.round(v * gain)); }
      var t = rgbOf(st.colour), tl = lum(t[0], t[1], t[2]);
      var wl = lum(244, 245, 247), bl = lum(27, 27, 30);
      var n = 0, hit = 0, hitW = 0, hitB = 0, gx, gy;
      for (gy = 0; gy < 3; gy++) for (gx = 0; gx < 4; gx++) {
        var ox = Math.round((FW - MW) * (gx + 0.5) / 4), oy = Math.round((FH - MH) * (gy + 0.5) / 3);
        var p = fx.getImageData(ox, oy, MW, MH).data;
        for (k = 3; k < m.length; k += 8) {
          if (m[k] < 128) continue;
          var L = lum(g8(p[k - 3]), g8(p[k - 2]), g8(p[k - 1]));
          n++;
          if (ratio(L, tl) < BLEND) hit++;
          if (ratio(L, wl) < BLEND) hitW++;
          if (ratio(L, bl) < BLEND) hitB++;
        }
      }
      if (!n) return res;
      var lim = isThin(st) ? LIMITS.thin : LIMITS.normal;
      res.share = hit / n;
      res.verdict = res.share >= lim.poor ? 'poor' : res.share >= lim.busy ? 'busy' : 'clear';
      /* Only name white or black if it is clearly better AND reads clean. */
      var light = tl > 0.4, altShare = light ? hitB / n : hitW / n;
      if (res.verdict === 'poor' && altShare < lim.busy && altShare < res.share * 0.5) {
        res.alt = light ? 'Black' : 'White';
      }
    } catch (e) { res.verdict = 'unknown'; }   /* image not readable: draw anyway */
    return res;
  }

  /* ---- drawing the thread ----------------------------------------------
     Flat text in a webfont is far crisper than thread: Matt's photo of white
     Block on Breaking Gas (27 Oct) shows soft, slightly grey satin with a
     visible stitch grain, ragged edges where the stitch ends sit, and a thin
     dark seam where it meets the fabric. Drawn crisp, white "pops" off a busy
     print in a way the real thing never does, which is exactly the mistake
     this preview exists to catch. So the letters are drawn as stitching:
       1. a faint dark seam round each letter, under the thread;
       2. the letter in the thread colour, a touch under full brightness;
       3. a fine satin grain across it, one stitch every ~0.3 mm, each stitch
          a slightly different shade, plus a little speckle;
       4. a dashed stroke round the outline for the stitch ends;
       5. composited with a sub-pixel blur so nothing is razor sharp.
     No drop shadow: that would do the legibility work the thread will not.
     The stroke weight is left as the font draws it: measured on that same
     photo, the satin columns are as wide as the webfont's (about 0.14 of
     the cap height on Block). */
  function glyphs(c, font, capNat, ds, ys, CW, how) {
    for (var i = 0; i < ds.length; i++) {
      var d = ds[i];
      c.save();
      c.translate(CW / 2, ys[i]);
      c.scale(d.stretch, 1);
      c.font = I.fontCss(font, 200 * (d.cap / capNat));
      c.textAlign = 'center';
      c.textBaseline = 'alphabetic';
      how(c, d);
      c.restore();
    }
  }
  function shade(rgb, k) {
    return 'rgb(' + Math.min(255, Math.round(rgb[0] * k)) + ',' +
      Math.min(255, Math.round(rgb[1] * k)) + ',' + Math.min(255, Math.round(rgb[2] * k)) + ')';
  }
  /* Seeded, so the grain does not crawl about every time a letter is typed. */
  function seeded(n) {
    return function () { n = (n * 1103515245 + 12345) & 0x7fffffff; return n / 0x7fffffff; };
  }
  function fillIt(g, d) { g.fillText(d.text, 0, 0); }

  function stitch(c, ds, ys, font, capNat, colour, ppc, CW, CH, small, gain) {
    gain = gain || 1;
    var rgb = rgbOf(colour), rnd = seeded(7), i;
    if (small) {               /* the 108 px thumbnail: too small for grain */
      c.save(); c.fillStyle = shade(rgb, 0.92);
      glyphs(c, font, capNat, ds, ys, CW, fillIt);
      c.restore();
      return;
    }
    var sp = Math.max(1.8, 0.04 * ppc);             /* px per stitch, ~0.4 mm */
    /* The bare fabric under the letters, before anything is drawn on it. */
    var FAB = document.createElement('canvas'); FAB.width = CW; FAB.height = CH;
    FAB.getContext('2d').drawImage(c.canvas, 0, 0);
    var dash = [];                                  /* irregular stitch ends */
    for (i = 0; i < 14; i++) dash.push(sp * (0.35 + rnd() * 0.9));

    /* 1. the seam, and the fuzz of loose fibre just outside the letter */
    c.save();
    c.lineJoin = 'round';
    c.strokeStyle = 'rgba(0,0,0,0.25)';
    glyphs(c, font, capNat, ds, ys, CW, function (g, d) {
      g.lineWidth = Math.max(1, d.cap * 0.07); g.strokeText(d.text, 0, 0);
    });
    if ('filter' in c) c.filter = 'blur(' + Math.max(0.5, ppc * 0.01).toFixed(2) + 'px)';
    c.strokeStyle = shade(rgb, 0.9);
    c.globalAlpha = 0.14;
    glyphs(c, font, capNat, ds, ys, CW, function (g, d) {
      g.lineWidth = Math.max(1.5, d.cap * 0.08); g.strokeText(d.text, 0, 0);
    });
    c.restore();

    /* 2. the thread, on its own layer so the grain stays inside the letters */
    var L = document.createElement('canvas'); L.width = CW; L.height = CH;
    var l = L.getContext('2d');
    l.fillStyle = shade(rgb, 0.97);
    glyphs(l, font, capNat, ds, ys, CW, fillIt);

    /* 3. satin grain: darker valleys between stitches, lighter crowns */
    l.globalCompositeOperation = 'source-atop';
    l.save();
    l.translate(CW / 2, CH / 2);
    l.rotate(-0.95);
    var R = Math.sqrt(CW * CW + CH * CH) / 2 + 4;
    for (var x = -R; x < R; x += sp) {
      l.lineWidth = sp * 0.38;
      l.strokeStyle = shade(rgb, 0.8 + rnd() * 0.1);
      l.beginPath(); l.moveTo(x, -R); l.lineTo(x, R); l.stroke();
      l.lineWidth = sp * 0.22;
      l.strokeStyle = shade(rgb, 1.0 + rnd() * 0.05);
      l.beginPath(); l.moveTo(x + sp * 0.5, -R); l.lineTo(x + sp * 0.5, R); l.stroke();
    }
    l.restore();
    try {
      var img = l.getImageData(0, 0, CW, CH), px = img.data;
      for (i = 0; i < px.length; i += 4) {
        if (!px[i + 3]) continue;
        var k = 0.92 + rnd() * 0.14;
        px[i] = Math.min(255, px[i] * k); px[i + 1] = Math.min(255, px[i + 1] * k); px[i + 2] = Math.min(255, px[i + 2] * k);
      }
      l.putImageData(img, 0, 0);
    } catch (e) {}

    /* 4. stitch ends along the outline, ragged */
    l.globalCompositeOperation = 'source-over';
    l.save();
    l.strokeStyle = shade(rgb, 0.84);
    l.setLineDash(dash);
    glyphs(l, font, capNat, ds, ys, CW, function (g, d) {
      g.lineWidth = Math.max(1, d.cap * 0.06); g.strokeText(d.text, 0, 0);
    });
    l.restore();

    /* 5. the print in the thread. Matt, 27 Oct: still too clear. On his photo
       the white thread stays bright over the navy ground but all but vanishes
       over the light anaesthetic machine, with the print's line work showing
       through it. A flat multiply could not do both (it greyed the thread on
       navy), so this is done per pixel:
         - the thread is multiplied by the COLOUR of the fabric under it,
           so a red print warms it but a dark ground does not grey it (MULTIPLY);
         - the closer the fabric under a pixel is to the thread colour, the
           more of it shows through (SHOW x similarity^1.3), so white on cream
           print merges, white on navy or yellow does not, and the same holds
           for any thread colour (black on black skull outlines, and so on).
       Tunable from the snippet config: fabricCheck.multiply / .show / .opacity. */
    try {
      var tp = l.getImageData(0, 0, CW, CH), td = tp.data;
      var tr = rgb[0] / 255, tg = rgb[1] / 255, tb = rgb[2] / 255;
      var fp = FAB.getContext('2d').getImageData(0, 0, CW, CH).data;
      for (i = 0; i < td.length; i += 4) {
        if (!td[i + 3]) continue;
        var fr = fp[i] / 255, fg = fp[i + 1] / 255, fb = fp[i + 2] / 255;
        var fl = Math.min(1, (0.3 * fr + 0.59 * fg + 0.11 * fb) * gain);
        /* Tint by the print's COLOUR, not its darkness: the fabric is
           normalised to its own brightest channel first, so black and navy
           grounds leave white thread white and a red bandana warms it. Dark
           grounds barely tint at all; light coloured print tints most. */
        var fm = Math.max(fr, fg, fb), mu = MULTIPLY * Math.min(1, fl * 1.6);
        if (fm > 0.15) {
          td[i]     = td[i]     * (1 - mu + mu * fr / fm);
          td[i + 1] = td[i + 1] * (1 - mu + mu * fg / fm);
          td[i + 2] = td[i + 2] * (1 - mu + mu * fb / fm);
        }
        /* How alike are thread and print here? White on cream or black on
           black merges; white on yellow or navy does not, however light. */
        var dr = tr - Math.min(1, fr * gain), dg = tg - Math.min(1, fg * gain), db = tb - Math.min(1, fb * gain);
        var sim = 1 - Math.sqrt(dr * dr + dg * dg + db * db) / 0.866;   /* 0.866 = half the cube diagonal */
        if (sim > 0) td[i + 3] = td[i + 3] * (1 - SHOW * Math.pow(sim, 1.3));
      }
      l.putImageData(tp, 0, 0);
    } catch (e) {}

    /* 6. onto the fabric, softened */
    c.save();
    if ('filter' in c) c.filter = 'blur(' + Math.max(0.6, ppc * 0.013).toFixed(2) + 'px)';
    c.globalAlpha = OPACITY;
    c.drawImage(L, 0, 0);
    c.restore();
  }

  function draw(rec, fab, st) {
    var font = M.FONTS[st.font] || M.FONTS.block;
    var ls = norm(st);
    var CW = rec.compact ? 216 : 900, CH = rec.compact ? 144 : 560;
    var cv = rec.cv, c = cv.getContext('2d');

    var widest = 0, i;
    for (i = 0; i < ls.length; i++) widest = Math.max(widest, M.widthCm(ls[i].text, st.font, ls[i].sizeMm));
    var blockCm = 0;
    for (i = 0; i < ls.length; i++) {
      blockCm += i ? Math.max(ls[i - 1].sizeMm, ls[i].sizeMm) / 10 * PITCH : ls[i].sizeMm / 10;
    }

    /* How much fabric to show. The lightbox shows at least 16 cm (a little more
       than the 12-13 cm the front of a cap takes), the button thumbnail is
       zoomed to the text so it can be read at 108 px. */
    var view = rec.compact ? Math.max(5, widest * 1.15) : Math.max(16, widest * 1.2);
    view = Math.max(view, (blockCm * 1.35) * CW / CH);
    var boxW = fab.box.w / fab.pxPerCm, boxH = fab.box.h / fab.pxPerCm;
    view = Math.min(view, boxW, boxH * CW / CH);

    var vw = view * fab.pxPerCm, vh = vw * CH / CW;
    var sx = fab.box.x + (fab.box.w - vw) / 2, sy = fab.box.y + (fab.box.h - vh) / 2;
    if (cv.width !== CW) cv.width = CW;
    if (cv.height !== CH) cv.height = CH;
    c.clearRect(0, 0, CW, CH);
    c.drawImage(fab.im, sx, sy, vw, vh, 0, 0, CW, CH);
    rec.viewCm = view;

    if (!ls.length) return { verdict: 'empty' };

    /* Same trick as the cap view: squash or stretch the drawing font until it
       is exactly as wide as the calibrated stitch width, so picture and meter
       tell the same story. */
    var ppc = CW / view;
    c.font = I.fontCss(font, 200);
    var capNat = I.capPx(font, 200);
    var ds = ls.map(function (l) {
      var natCm = (c.measureText(l.text).width / capNat) * (l.sizeMm / 10);
      var want = M.widthCm(l.text, st.font, l.sizeMm);
      return { text: l.text, cap: (l.sizeMm / 10) * ppc, stretch: natCm > 0 ? want / natCm : 1 };
    });
    var total = ds[0].cap;
    for (i = 1; i < ds.length; i++) total += Math.max(ds[i - 1].cap, ds[i].cap) * PITCH;
    var y0 = (CH - total) / 2 + ds[0].cap - ds[ds.length - 1].cap * 0.08;
    var ys = [y0];
    for (i = 1; i < ds.length; i++) ys.push(ys[i - 1] + Math.max(ds[i - 1].cap, ds[i].cap) * PITCH);

    /* ---- how much of the print around the letters meets the thread ------
       Where the text lands on the print depends on how the cap is cut, so it
       is not judged on the one spot drawn above: the same block is laid over
       twelve spots across the whole flat lay, at the same scale, and the
       share of the area round the letters that is close to the thread colour
       is averaged. The thumbnail has no words to show, so it skips this. */
    var res = rec.compact ? { verdict: 'skip' } : judge(fab, font, capNat, ds, ys, ppc, st, CW);

    /* ---- the thread ----------------------------------------------------- */
    stitch(c, ds, ys, font, capNat, st.colour || '#f2f3f5', ppc, CW, CH, rec.compact, fab.gain);
    return res;
  }

  /* ---- mounting --------------------------------------------------------- */
  function say(rec, res, st) {
    if (!rec.read) return;
    var txt = '';
    if (res.verdict === 'clear') txt = COPY.sayClear;
    else if (res.verdict === 'busy') txt = COPY.sayBusy + (isThin(st) ? COPY.thinTip : COPY.sizeTip);
    else if (res.verdict === 'poor') {
      txt = COPY.sayPoor + (res.alt ? COPY.altTip.replace('{colour}', res.alt) : '') +
            (isThin(st) ? COPY.thinTip : '') + COPY.outlineTip;
    }
    if (rec.read.textContent !== txt) rec.read.textContent = txt;
    var sc = rec.fabExact
      ? COPY.exact.replace('{cm}', Math.round(rec.viewCm || 16))
      : COPY.approx;
    if (rec.scale.textContent !== sc) rec.scale.textContent = sc;
    rec.badge.textContent = res.verdict === 'clear' ? COPY.clear
      : res.verdict === 'busy' ? COPY.busy : res.verdict === 'poor' ? COPY.poor : '';
  }

  function render(rec) {
    var st = rec.api.state;
    var seq = rec.seq = (rec.seq || 0) + 1;
    Promise.all([fabric(), M.loadFont(st.font)]).then(function (r) {
      if (seq !== rec.seq) return;
      var fab = r[0];
      rec.fabExact = fab.exact;
      var res = draw(rec, fab, st);
      var v = (res.verdict === 'unknown' || res.verdict === 'skip') ? 'empty' : res.verdict;
      rec.share = res.share;
      rec.host.setAttribute('data-mc-share', Math.round((res.share || 0) * 100));
      if (rec.host.getAttribute('data-mc-read') !== v) rec.host.setAttribute('data-mc-read', v);
      rec.cv.setAttribute('aria-label', norm(st).length
        ? norm(st).map(function (l) { return l.text; }).join(', ') + ' over the fabric of this cap'
        : 'The fabric of this cap');
      say(rec, res, st);
    }).catch(function () {
      /* No usable fabric: leave the old cap view exactly as it was. */
      rec.host.classList.remove('mc-embf--fabric');
      if (rec.tabs) rec.tabs.hidden = true;
      if (rec.thumb) rec.thumb.classList.remove('mc-embf-thumb');
    });
  }

  function setMode(rec, mode) {
    rec.mode = mode;
    rec.host.classList.toggle('mc-embf--fabric', mode === 'fabric');
    rec.tabFabric.setAttribute('aria-pressed', mode === 'fabric' ? 'true' : 'false');
    rec.tabCap.setAttribute('aria-pressed', mode === 'cap' ? 'true' : 'false');
  }

  function attach(host, opts, api) {
    var cv = el('canvas', 'mc-embf__canvas');
    cv.setAttribute('role', 'img');
    var rec = { host: host, api: api, cv: cv, compact: !!opts.compact };

    if (rec.compact) {
      /* Only the button thumbnail on a product page. The size ladder on the
         guide page is compact too, and has no fabric, so it is left alone. */
      var thumb = host.parentNode;
      if (!thumb || !thumb.classList || !thumb.classList.contains('mc-emb-cue__thumb')) return;
      rec.thumb = thumb;
      thumb.classList.add('mc-embf-thumb');
      thumb.appendChild(cv);
    } else {
      var stage = host.querySelector('.mc-emb__stage');
      if (!stage) return;
      stage.insertBefore(cv, stage.firstChild);
      rec.badge = el('span', 'mc-embf__badge');
      stage.appendChild(rec.badge);

      var tabs = rec.tabs = el('div', 'mc-embf__tabs');
      tabs.setAttribute('role', 'group');
      tabs.setAttribute('aria-label', 'Preview view');
      rec.tabFabric = el('button', 'mc-embf__tab', COPY.tabFabric);
      rec.tabCap = el('button', 'mc-embf__tab', COPY.tabCap);
      rec.tabFabric.type = rec.tabCap.type = 'button';
      tabs.appendChild(rec.tabFabric); tabs.appendChild(rec.tabCap);
      host.insertBefore(tabs, stage);
      rec.tabFabric.addEventListener('click', function () { setMode(rec, 'fabric'); });
      rec.tabCap.addEventListener('click', function () { setMode(rec, 'cap'); });

      var info = el('div', 'mc-embf__info');
      rec.read = el('p', 'mc-embf__read');
      rec.scale = el('p', 'mc-embf__scale');
      info.appendChild(rec.read); info.appendChild(rec.scale);
      stage.parentNode.insertBefore(info, stage.nextSibling);
      setMode(rec, 'fabric');
    }
    host.classList.add('mc-embf');
    if (rec.compact) host.classList.add('mc-embf--fabric');

    var up = api.update, redraw = api.redraw;
    api.update = function (patch) {
      var changed = up.call(api, patch);
      if (changed) render(rec);
      return changed;
    };
    api.redraw = function () { redraw.call(api); render(rec); };
    render(rec);
  }

  var mount = M.mount;
  M.mount = function (host, opts) {
    var api = mount.apply(this, arguments);
    try { attach(host, opts || {}, api); } catch (e) { /* never break the cap view */ }
    return api;
  };
})();
